import {
  describeApiError,
  FIND_BY_CARDS_MAX,
  FIND_BY_INTERNAL_IDS_MAX,
  fetchAllPages,
  MAX_PAGE_SIZE,
  MAX_PAGES_READ,
  retryUnlessClientError,
} from '@brush/api-client';
import { findByCardsInBatches } from './import/cardLookup';
import { saveFile } from './export/saveFile';
import { type CandidateParams } from './leaderboards/candidates';
import { useActingRole } from '@brush/auth';
import type {
  ApplyTournamentTagsRequest,
  CreateFantasyContestRequest,
  CreateLeaderboardRequest,
  CreatePlayerRequest,
  ExportChoice,
  ExportTarget,
  CommitPlayerImportRequest,
  PatchPlayerRequest,
  PreviewPlayerImportRequest,
  CreatePointsSystemRequest,
  CreateSalaryModelRequest,
  CreateRatingBoardRequest,
  PutRatingBoardRequest,
  CreateTournamentRequest,
  PatchFantasyContestRequest,
  PatchRoomRequest,
  PublishFantasyContestRequest,
  PutFantasyPoolRequest,
  PatchTournamentRequest,
  PutLeaderboardRequest,
  PutRoomTemplateContentRequest,
  RecalcMode,
  StartLeaderboardRequest,
  RoomTournamentListParams,
  TournamentResult,
} from '@brush/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { overviewKey, rundownKey, toSummaryQuery } from '@brush/ui';
import { useApi } from './api';
import { activityRefresh } from './activity/refresh';
import { usePeriodChoice } from './periods/context';
import { summaryRefresh } from './summary';
import { sameRoom } from './placeholder';
import {
  deleteTournament,
  EMPTY_TOURNAMENT_PATCH,
  importResults,
  patchTournament,
  type ResultsImportOutcome,
  type TournamentTarget,
} from './tournamentWrites';

/**
 * Which build the API is running, for the About panel. Enabled only while the panel is open — it
 * is the one place the answer is shown, and an app that asked on every load would spend a request
 * per session on a screen most people never open.
 *
 * `staleTime: Infinity`: a running API does not change build under a page. A reload is what picks
 * up a new one, and a reload refetches anyway.
 */
export function useApiVersion(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['api-version'],
    queryFn: () => api.version(),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Who the caller is, including which rooms they may operate. Administrators get every room;
 * operators get only their assignments.
 */
export function useCurrentUser() {
  const api = useApi();
  const { actingRole } = useActingRole();

  // Keyed on the acting role, because the answer depends on it — an administrator acting as an
  // operator is told about their assigned rooms rather than every room. It is also what makes
  // recovery from a stale choice automatic: clearing the role changes the key, so the refetch
  // that discovers what the account may still do needs nobody to remember to ask for it.
  return useQuery({
    queryKey: ['me', actingRole ?? 'unchosen'],
    queryFn: () => api.me(),
    staleTime: 5 * 60_000,
    // A refused choice is answered by clearing it, not by asking again with the same header.
    retry: false,
  });
}

/** `enabled` is whether a role is in force (`roleInForce`): behind the role chooser the list is not read (#544). */
export function useRooms(enabled: boolean) {
  const api = useApi();
  return useQuery({ queryKey: ['rooms'], queryFn: () => api.rooms.list(), enabled });
}

/**
 * What the shell and Overview draw — the section counts, the tournament totals, the rundown, the running
 * leaderboards and the contests in flight — computed by the server at the room's gaming day
 * (`GET /api/rooms/{roomId}/summary`, performance rules 3, 4 and 13). One small read in place of the five
 * whole lists the shell used to fetch on every section.
 *
 * Data class: changes on writes AND with time. Fresh for five minutes or until the room's day ends, and
 * asked again at that boundary (`summaryRefresh`); this app's own writes invalidate it at once
 * (`invalidateRoomSummary`, held by `queries.summary.test.js`). A 4xx is an answer and is not retried.
 *
 * **One request carries both periods** (user-preferences.md UP22): the session's Rundown and Overview choices
 * (`usePeriodChoice`), so the shell, the rail and Overview read one cached answer. They are discriminators AFTER the
 * room id, so `invalidateRoomSummary`'s prefix still reaches every one. Within one room the previous answer stays on
 * screen while a new period is asked for, so the badges — which no period moves (UP10) — never flash to zero.
 */
export function useRoomSummary(roomId: string | null) {
  const api = useApi();
  const [rundown] = usePeriodChoice('rundown.period');
  const [totals] = usePeriodChoice('overview.totals');
  return useQuery({
    queryKey: ['room-summary', roomId, rundownKey(rundown), overviewKey(totals)],
    queryFn: () => api.rooms.summary(roomId ?? '', toSummaryQuery(rundown, totals)),
    enabled: !!roomId,
    placeholderData: sameRoom(roomId),
    ...summaryRefresh,
    retry: retryUnlessClientError,
  });
}

/**
 * Every write that changes what the summary counts, sums or lists calls this beside its own
 * invalidation, so the badges, the rundown and Overview move with the write rather than up to five
 * minutes later. `queries.summary.test.js` fails a write that forgets.
 */
function invalidateRoomSummary(qc: ReturnType<typeof useQueryClient>, roomId: string | null) {
  void qc.invalidateQueries({ queryKey: ['room-summary', roomId] });
}

/**
 * The standings a tournament write can move ([#529](https://github.com/GoldenZiggurat/brush/issues/529)). The server follows a results write, a place claim, a bulk tag
 * change, a tournament edit or a delete with `TournamentChangeFollowOns`: every running leaderboard and rating
 * board on result entry with the tournament in scope publishes a new snapshot before the response returns, and
 * each leaderboard projects its locked contests. Which boards counted the tournament is the server's to know, so
 * every snapshot-derived key goes by prefix. `queries.standings.test.js` fails a write that forgets.
 */
function invalidateRecalculated(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['leaderboard-standings'] });
  void qc.invalidateQueries({ queryKey: ['leaderboard-snapshots'] });
  void qc.invalidateQueries({ queryKey: ['leaderboard-drift'] });
  void qc.invalidateQueries({ queryKey: ['rating-snapshot'] });
  void qc.invalidateQueries({ queryKey: ['rating-standings'] });
  void qc.invalidateQueries({ queryKey: ['rating-drift'] });
  void qc.invalidateQueries({ queryKey: ['fantasy-standings'] });
  void qc.invalidateQueries({ queryKey: ['fantasy-drift'] });
}

/**
 * The tournaments a leaderboard's or a rating's scope could score, for the scope filters' tick list (known gap
 * 35): the room's list without results, read page after page — 200 at a time, in date order, one after another
 * — until it holds the whole matched set, so a year-long board or a rating with open bounds is listed in full.
 * `params` is what `candidateParams` makes of the **debounced** scope; exclusions and ticks are applied by the
 * component, so ticking a row asks for nothing.
 *
 * Keyed under `['tournaments', roomId]` so every tournament write's invalidation reaches it. The previous answer
 * stays on screen while a new one loads (the screen says it is updating), and a later page failing answers what
 * was read with `complete: false`, which the screen states — it is not retried by itself.
 */
export function useScopeCandidates(roomId: string | null, params: CandidateParams) {
  const api = useApi();
  return useQuery({
    queryKey: ['tournaments', roomId, 'candidates', params],
    // The signal is what stops a superseded read between pages: consuming it is also what lets TanStack
    // cancel this query when a scope edit moves the key.
    queryFn: ({ signal }) =>
      fetchAllPages(
        (page, pageSize) =>
          api.tournaments.listForRoom(roomId ?? '', { ...params, page, pageSize, sort: 'date', dir: 'asc' }),
        MAX_PAGE_SIZE,
        MAX_PAGES_READ,
        signal,
      ),
    enabled: !!roomId,
    staleTime: 60_000,
    retry: retryUnlessClientError,
    placeholderData: sameRoom(roomId),
  });
}

/**
 * One filtered page of the room's tournament list, as a list shape: the rows the table draws, without
 * each tournament's results or sittings (`GET /api/rooms/{roomId}/tournaments`, performance rule 2) —
 * a page of 25 is about 10 KB where it was over half a megabyte. A date preset is resolved by the server
 * at the room's gaming day, and the page says which dates it applied (`from`, `to`).
 *
 * `roomId` stays in position 1, matching every other tournament key, so the mutations'
 * `['tournaments', roomId]` invalidation reaches these pages by prefix. Putting the
 * discriminator first instead silently exempts the list from every refresh.
 *
 * Data class: changes on writes — fresh for a minute, invalidated by this app's own writes at once.
 */
export function useRoomTournamentPage(roomId: string | null, params: RoomTournamentListParams) {
  const api = useApi();
  return useQuery({
    queryKey: ['tournaments', roomId, 'page', params],
    queryFn: () => api.tournaments.listForRoom(roomId ?? '', params),
    enabled: !!roomId,
    staleTime: 60_000,
    retry: retryUnlessClientError,
    // Keep the previous page on screen while the next one loads, so paging doesn't blink
    // the table out and collapse the page height under the cursor.
    placeholderData: sameRoom(roomId),
  });
}

/**
 * One tournament with its results and sittings — what the detail view and the results editors read,
 * now the page carries neither. Keyed under the room's tournaments, so every write's
 * `['tournaments', roomId]` invalidation refreshes it.
 *
 * **The staff read, with the cards** (#494): the detail's Card ID column, the editors' seeded cards and the
 * unclaimed places' source cards all come from here. The member read (`tournaments.get`) carries none, so on it
 * every named finisher would read *No card ID*. `queries.cards.test.js` holds this hook to the staff route.
 *
 * Data class: changes on writes. A 404 (another room's id, or one deleted) is an answer, not retried.
 */
export function useTournament(roomId: string | null, id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['tournaments', roomId, 'item', id],
    queryFn: () => api.tournaments.staffDetail(id ?? ''),
    enabled: !!roomId && !!id,
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

/**
 * The room's tag vocabulary with how many tournaments carry each tag — what the tag inputs rank by and
 * the filter bar lists (`GET /api/rooms/{roomId}/tournament-tags`, performance rule 3), in place of a
 * count over the whole capped tournament list. Under the room's tournament key, so a write's
 * invalidation refreshes it.
 *
 * Data class: changes on writes.
 */
export function useTournamentTagUsage(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['tournaments', roomId, 'tags'],
    queryFn: () => api.tournaments.tagUsage(roomId ?? ''),
    enabled: !!roomId,
    staleTime: 5 * 60_000,
    retry: retryUnlessClientError,
  });
}

/**
 * The room's "today" — its gaming day, from the server (ADR 0014, performance rule 13) — and when the next
 * begins. Data class: changes with time, so it is fresh until that boundary and asked again at it, the
 * same rule as the summary's (`summaryRefresh`).
 */
export function useRoomToday(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['room-today', roomId],
    queryFn: () => api.rooms.today(roomId ?? ''),
    enabled: !!roomId,
    ...summaryRefresh,
    retry: retryUnlessClientError,
  });
}

export function useLeaderboards(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboards', roomId],
    queryFn: () => api.leaderboards.list(roomId ?? undefined),
    enabled: !!roomId,
  });
}

/**
 * The operator Overview's activity cards (ADR 0012 Decision 16, step 20): this room's own figures, counts
 * only.
 *
 * Data class: changes with time (performance rule 12). Fresh for a minute — the figures already lag about
 * fifteen seconds and a minute more is still the month so far — never past the room's next day, stale at once
 * when the summary names a day other than the one they were computed at, and asked again at that boundary
 * (`activityRefresh`). The room's day comes from the summary the shell already reads, observed rather than
 * waited for, so this read still runs beside it. A recalculation, a close and a trigger change invalidate it at
 * once. A 4xx is an answer and is not retried.
 *
 * With the session's Overview period (UP22) as a discriminator after the room id, so the invalidations' prefix
 * still reaches it, and the previous answer kept on screen within one room while a new period loads.
 */
export function useRoomActivity(roomId: string | null) {
  const api = useApi();
  const roomDay = useRoomSummary(roomId).data;
  return useQuery({
    queryKey: ['room-activity', roomId],
    queryFn: () => api.activity.room(roomId!),
    enabled: !!roomId,
    ...activityRefresh(roomDay),
    retry: retryUnlessClientError,
  });
}

export function useFantasyContests(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-contests', roomId],
    queryFn: () => api.fantasyContests.list(roomId ?? undefined),
    enabled: !!roomId,
  });
}

/**
 * The room's players matching `term` — card, first name, last name, city or state — by surname, from
 * the server (`POST /api/rooms/{roomId}/players/search`, performance rank 2). What the player pickers
 * search, in place of the room's whole roster. A blank term answers the first players by surname.
 *
 * The caller debounces the term; each distinct term is its own cache entry, and the previous answer is
 * kept on screen meanwhile (`placeholderData`) so the caller can tell a stale answer from a fresh one.
 * Under `['players', roomId]`, so a player created or renamed is found by the next search.
 */
export function useRoomPlayerSearch(roomId: string | null, term: string, enabled = true) {
  const api = useApi();
  const query = term.trim();
  return useQuery({
    queryKey: ['players', roomId, 'search', query],
    queryFn: () => api.players.searchRoom(roomId ?? '', { query: query === '' ? null : query, limit: 20 }),
    enabled: !!roomId && enabled,
    placeholderData: sameRoom(roomId),
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

/**
 * The room's players holding the cards a results file names (`POST …/players/by-cards`, performance
 * rank 2), asked in batches of `FIND_BY_CARDS_MAX` and merged, so no file is too large (gate addition A2).
 * What the results import matches its file against, in place of the room's whole roster. Null cards
 * asks nothing. Never fresher than the moment it is asked: an import must see the room as it is.
 */
export function useCardHolders(roomId: string | null, cards: readonly string[] | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['players', roomId, 'by-cards', cards],
    queryFn: () =>
      findByCardsInBatches(cards ?? [], FIND_BY_CARDS_MAX, (batch) => api.players.findByCards(roomId ?? '', batch)),
    enabled: !!roomId && cards !== null,
    staleTime: 0,
    retry: retryUnlessClientError,
  });
}

/**
 * The room's tournaments holding the internal IDs a file names (`fileInternalIds`), sittings and tags included
 * and no results — what the tournament import compares its file against (known gap 108), in place of the room's
 * capped list with every result. Null IDs (no file yet) ask nothing; no IDs answer an empty room without asking.
 * At most `FIND_BY_INTERNAL_IDS_MAX`, which is the importer's own limit on a file: a file naming more is refused
 * by the plan whatever this answers.
 *
 * Keyed under `['tournaments', roomId]`, so a run's own writes refetch it. Never cached across files: what
 * the room holds is the question a re-import asks, so it is asked each time.
 */
export function useHeldTournaments(roomId: string | null, internalIds: readonly string[] | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['tournaments', roomId, 'held', internalIds],
    queryFn: () =>
      (internalIds ?? []).length === 0
        ? Promise.resolve([])
        : api.tournaments.findByInternalIds(roomId ?? '', (internalIds ?? []).slice(0, FIND_BY_INTERNAL_IDS_MAX)),
    enabled: !!roomId && internalIds !== null,
    staleTime: 0,
    retry: retryUnlessClientError,
  });
}

/**
 * One page of the room's players with the contact the room holds, by surname, narrowed by `term`
 * (`POST …/players/records`, the Players screen's Phase 0). The Players screen, in place of reading every
 * player in the room and filtering in the browser.
 *
 * The key carries the term, the page and the size, so each is its own entry, under `['players', roomId]`
 * so a player created or edited refetches the page on screen. The previous page stays on screen while the
 * next is on its way (`placeholderData`), which the screen dims rather than blanking. A minute fresh: a
 * write here invalidates it, and another operator's edit reaches it on the next page change.
 */
export function useRoomPlayerPage(roomId: string | null, term: string, page: number, pageSize: number) {
  const api = useApi();
  const query = term.trim();
  return useQuery({
    queryKey: ['players', roomId, 'records', { term: query, page, pageSize }],
    queryFn: () => api.players.records(roomId ?? '', { query: query === '' ? null : query, page, pageSize }),
    enabled: !!roomId,
    placeholderData: sameRoom(roomId),
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

/**
 * How many of this room's players can be sent a verification code, which is how many of them can
 * ever claim their card. Without the number a room reads an unpopulated feature as a broken one.
 */
export function useContactCoverage(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['players', roomId, 'coverage'],
    queryFn: () => api.players.contactCoverage(roomId ?? ''),
    enabled: !!roomId,
  });
}

/**
 * Who has connected a sign-in to one of this room's cards (ADR 0010, Decision 7).
 *
 * Keyed under `players` because that is the area it is read on and the thing it is about — so
 * revoking, which changes who holds a card and nothing about the card, is reached by the same
 * prefix invalidation as everything else on that screen.
 */
export function useRoomMemberships(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['players', roomId, 'memberships'],
    queryFn: () => api.memberships.listForRoom(roomId ?? ''),
    enabled: !!roomId,
  });
}

/**
 * Takes one card back from the account holding it. The same row the player's own *Disconnect*
 * removes, and a different act — this one is a decision about somebody else, which is why the
 * route is room-scoped and the screen asks twice.
 *
 * Nothing but the membership list needs invalidating: no result, standing or snapshot moves, and
 * an invalidation that swept the players list too would suggest otherwise.
 */
export function useRevokeMembership(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playerId: string) => api.memberships.revoke(roomId ?? '', playerId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['players', roomId, 'memberships'] }),
  });
}

// ---------- templates (platform-defined; this room supplies its own content) ----------

/** Every template with this room's content for it, or null where it has not been filled in. */
export function useRoomTemplates(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['room-templates', roomId],
    queryFn: () => api.roomTemplates.list(roomId ?? ''),
    enabled: !!roomId,
  });
}

export function useSaveTemplateContent(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, body }: { templateId: string; body: PutRoomTemplateContentRequest }) =>
      api.roomTemplates.save(roomId ?? '', templateId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['room-templates', roomId] }),
  });
}

export function useClearTemplateContent(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.roomTemplates.clear(roomId ?? '', templateId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['room-templates', roomId] }),
  });
}

// ---------- platform tags (suggestions; rooms can apply but not remove) ----------

export function usePlatformTags() {
  const api = useApi();
  return useQuery({ queryKey: ['platform-tags'], queryFn: () => api.platformTags.list() });
}

// ---------- tournament mutations ----------

export function useCreateTournament(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTournamentRequest) => api.tournaments.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

export function usePatchTournament(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // Refused unsent when the tournament is not the room on screen's (#591, `tournamentWrites.ts`).
    mutationFn: ({ target, body }: { target: TournamentTarget; body: Partial<PatchTournamentRequest> }) =>
      patchTournament(api, roomId, target, body),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      // Posting results recomputes prize pool, entries, and player aggregates — and may add or
      // claim unclaimed places.
      if (variables.body.results) {
        void qc.invalidateQueries({ queryKey: ['players', roomId] });
        void qc.invalidateQueries({ queryKey: ['unclaimed-places', roomId] });
      }
      invalidateRecalculated(qc);
    },
  });
}

/** Every place in the room's results that nobody has claimed yet (ADR 0013). */
export function useUnclaimedPlaces(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['unclaimed-places', roomId],
    queryFn: () => api.tournaments.unclaimedPlaces(roomId!),
    enabled: !!roomId,
  });
}

/**
 * Claims a place for a player, or returns it to unclaimed. The same side effects as a results save —
 * the tournament, the room's player totals and the list of places left to claim.
 */
export function useAssignPlace(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tournamentId, position, resultId, playerId }: {
      tournamentId: string;
      position: number;
      resultId: string;
      playerId: string | null;
    }) => api.tournaments.assignPlace(tournamentId, position, { resultId, playerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['players', roomId] });
      void qc.invalidateQueries({ queryKey: ['unclaimed-places', roomId] });
      invalidateRecalculated(qc);
    },
  });
}

export type { ResultsImportOutcome };

/**
 * Importing a file of finishing positions into one tournament: `importResults` in `tournamentWrites.ts`, which
 * says why players are created first and why the room is checked before either.
 */
export function useImportResults(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      target: TournamentTarget;
      newPlayers: CreatePlayerRequest[];
      /** Turns card-id → player-id into the results to write, once every player exists. */
      build: (idsByCard: Map<string, string>) => TournamentResult[];
      onProgress?: (done: number) => void;
    }): Promise<ResultsImportOutcome> => importResults(api, roomId, { ...input, describeError: describeApiError }),
    // On settled: players may exist even when the results write failed, so the player list behind
    // the dialog is stale either way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['players', roomId] });
      void qc.invalidateQueries({ queryKey: ['unclaimed-places', roomId] });
      invalidateRecalculated(qc);
    },
  });
}

/**
 * Bulk tag change over whatever the filter currently matches.
 *
 * Invalidates the whole `['tournaments', roomId]` prefix rather than the paged key alone: this
 * writes to rows the operator cannot see, and it can introduce or retire a tag, so the room-wide
 * list feeding the tag vocabulary is stale too.
 */
export function useApplyTournamentTags(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplyTournamentTagsRequest) => api.tournaments.applyTags(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      invalidateRecalculated(qc);
    },
  });
}

/**
 * One thing the import will do. Tournaments the file leaves untouched never become an operation,
 * so a re-import of an unchanged schedule sends nothing at all.
 *
 * There is no delete: a tournament the room holds and the file doesn't mention is left alone. An
 * import file is one person's list, not the authority on what the room is running.
 */
export type ImportOperation =
  | { kind: 'create'; internalId: string; body: CreateTournamentRequest }
  | { kind: 'update'; internalId: string; id: string; body: Partial<PatchTournamentRequest> };

/** What one file's worth of tournaments did. Failures name the row, not a generated id. */
export interface ImportOutcome {
  created: number;
  updated: number;
  failures: { internalId: string; message: string }[];
}

/**
 * Applies every operation a validated file implies.
 *
 * One request at a time, and it does not stop at the first rejection. Sequential because the
 * writes cross the Hybrid Connection to the on-premises server and there is nothing to gain from
 * racing them; carrying on because the plan was validated before it got here, so a rejection is
 * something specific about one row — reporting which ones is more use than abandoning the rest
 * part-way with no account of where it stopped.
 *
 * There is no server-side import, so this is not atomic: a run that hits a failure leaves the
 * work before it applied. The outcome says exactly what, and the caller shows it.
 */
export function useImportTournaments(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      operations,
      onProgress,
    }: {
      operations: ImportOperation[];
      onProgress?: (done: number) => void;
    }): Promise<ImportOutcome> => {
      let created = 0;
      let updated = 0;
      const failures: ImportOutcome['failures'] = [];

      for (const operation of operations) {
        try {
          if (operation.kind === 'create') {
            await api.tournaments.create(operation.body);
            created += 1;
          } else {
            // The contract wants every field present, with null meaning "leave unchanged" — so
            // the plan's sparse patch is filled out against the same blank the console uses.
            await api.tournaments.patch(operation.id, {
              ...EMPTY_TOURNAMENT_PATCH,
              ...operation.body,
            });
            updated += 1;
          }
        } catch (err) {
          failures.push({
            internalId: operation.internalId,
            message: describeApiError(err) ?? 'That request was rejected.',
          });
        }
        onProgress?.(created + updated + failures.length);
      }

      return { created, updated, failures };
    },
    // On settled rather than on success: a partial run has still changed the room, and the list
    // behind the dialog must not go on showing the schedule as it was before it. Only a create
    // moves the room's tournament count, but an update can move a tournament off the filtered
    // page on screen, so both invalidate the same keys.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      invalidateRecalculated(qc);
    },
  });
}

export function useDeleteTournament(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: TournamentTarget) => deleteTournament(api, roomId, target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      invalidateRecalculated(qc);
    },
  });
}

// ---------- leaderboards ----------

/**
 * Standings are computed server-side — the client never recomputes points. The staff read, with each player's card
 * (#494): the leaderboard detail shows it under each name, and the member standings carry none.
 */
export function useLeaderboardStandings(leaderboardId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboard-standings', leaderboardId],
    queryFn: () => api.leaderboards.staffStandings(leaderboardId ?? ''),
    enabled: !!leaderboardId,
  });
}

export function useCreateLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaderboardRequest) => api.leaderboards.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leaderboards', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

export function useUpdateLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PutLeaderboardRequest }) =>
      api.leaderboards.update(id, body),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['leaderboards', roomId] });
      invalidateRoomSummary(qc, roomId);
      // Formula or tournament changes move the standings.
      void qc.invalidateQueries({ queryKey: ['leaderboard-standings', variables.id] });
    },
  });
}

export function useDeleteLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.leaderboards.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leaderboards', roomId] });
      invalidateRoomSummary(qc, roomId);
      // Fantasy contests attach to leaderboards, so they may be affected.
      void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
    },
  });
}

// ---------- leaderboard lifecycle: the pin, snapshots and drift (§8, §9.9) ----------

/**
 * Everything a leaderboard's lifecycle touches, invalidated together.
 *
 * Starting, closing and recalculating all write a snapshot or move the status, and every one of
 * them changes what the standings and the drift indicator say. Listing them once is what stops the
 * next trigger being added with two of the four remembered — which shows up as a screen that is
 * right until you reload it.
 */
function invalidateLifecycle(qc: ReturnType<typeof useQueryClient>, roomId: string | null, id: string) {
  void qc.invalidateQueries({ queryKey: ['leaderboards', roomId] });
  invalidateRoomSummary(qc, roomId);
  void qc.invalidateQueries({ queryKey: ['leaderboard-standings', id] });
  void qc.invalidateQueries({ queryKey: ['leaderboard-snapshots', id] });
  void qc.invalidateQueries({ queryKey: ['leaderboard-drift', id] });
  // The Overview's drift card (step 23) lists boards behind their room's tournaments; a recalculation,
  // a close or a change of trigger can take a board off it.
  void qc.invalidateQueries({ queryKey: ['room-activity', roomId] });
}

/**
 * §2.3's pin, taken at the transition to Running. The system is chosen here rather than saved on
 * the draft, because this is the moment it stops being a choice.
 */
export function useStartLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StartLeaderboardRequest }) =>
      api.leaderboards.start(id, body),
    onSuccess: (_result, variables) => invalidateLifecycle(qc, roomId, variables.id),
  });
}

/** §8: writes the final snapshot, which determines prizes. */
export function useCloseLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.leaderboards.close(id),
    onSuccess: (_result, id) => invalidateLifecycle(qc, roomId, id),
  });
}

/** §9.9's one control that stays editable while a board runs. */
export function useSetLeaderboardRecalculation(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, recalcMode, recalcCron }: {
      id: string;
      recalcMode: RecalcMode;
      /** Null unless the mode is `scheduled` — the server refuses a schedule beside a mode that never reads one. */
      recalcCron: string | null;
    }) => api.leaderboards.setRecalculation(id, { recalcMode, recalcCron }),
    onSuccess: (_result, variables) => invalidateLifecycle(qc, roomId, variables.id),
  });
}

/**
 * §7.5's Recalculate now. `reason` is ignored on a running board and required on a closed one,
 * where an administrator is overriding §8.2's block — the server decides which, and its refusal is
 * what the screen shows.
 */
export function useRecalculateLeaderboard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string | null }) =>
      api.leaderboards.recalculate(id, { reason: reason ?? null }),
    onSuccess: (_result, variables) => invalidateLifecycle(qc, roomId, variables.id),
  });
}

export function useLeaderboardSnapshots(leaderboardId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboard-snapshots', leaderboardId],
    queryFn: () => api.leaderboards.snapshots(leaderboardId ?? ''),
    enabled: !!leaderboardId,
  });
}

/**
 * §8.1. Live computation diffed against the latest snapshot.
 *
 * **Only asked for when the caller is an administrator**, which is what the endpoint requires and
 * what §8.1 asks for ("surface drift as an admin alert"). An operator's screen does not fire a
 * request it knows will be a 403 — a permanent error notice on a leaderboard reads as a broken app
 * rather than as a feature they don't have.
 *
 * It runs a full live computation, so it is not refetched on focus: it is a report on what changed
 * since the last recalculation, and that question is answered when the screen opens.
 */
export function useLeaderboardDrift(leaderboardId: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['leaderboard-drift', leaderboardId],
    queryFn: () => api.leaderboards.drift(leaderboardId ?? ''),
    enabled: enabled && !!leaderboardId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

/**
 * §9.9's live "N events in scope", and the events behind it.
 *
 * Keyed on the scope document, which is what changes the answer, and debounced by the caller for
 * the same reason the preview is: it shares the preview endpoint's per-caller rate limit, so
 * returning to a scope already resolved should cost no request. `retry: false` because the one
 * failure worth expecting is that limit, and retrying into it is how a caller stays refused.
 */
export function useScopeEvents({
  roomId,
  scope,
  enabled,
}: {
  roomId: string | null;
  scope: unknown;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    queryKey: ['points-scope-events', roomId, JSON.stringify(scope)],
    queryFn: () => api.pointsSystems.scopeEvents(roomId ?? '', scope),
    enabled: enabled && !!roomId && scope !== null,
    staleTime: Infinity,
    retry: false,
  });
}

// ---------- points systems (this room's own, plus the platform's) ----------

/**
 * What this room may score with: its own systems and every platform one, in a single ordered
 * list. The server merges them — §2.2's picker is one query, not two lists the client has to
 * reconcile and sort for itself.
 */
export function usePointsSystems(roomId: string | null, includeArchived: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['points-systems', roomId, { includeArchived }],
    queryFn: () => api.pointsSystems.listForRoom(roomId ?? '', includeArchived),
    enabled: !!roomId,
  });
}

/**
 * One version's definition. The list carries only summaries — number, status, dates — since a
 * list of systems has no business shipping every stored definition with it.
 */
export function usePointsSystemVersion(systemId: string | null, versionNumber: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['points-system-version', systemId, versionNumber],
    queryFn: () => api.pointsSystems.version(systemId ?? '', versionNumber ?? 0),
    enabled: !!systemId && versionNumber !== null,
    // A published version is frozen and a draft only changes from the builder, which invalidates
    // on save. Nothing else moves it underneath us.
    staleTime: 5 * 60_000,
  });
}

export function useCreatePointsSystem(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePointsSystemRequest) =>
      api.pointsSystems.createForRoom(roomId ?? '', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

/**
 * §2.2's answer to a platform system an operator wants to change: take a copy this room owns.
 *
 * The copy lands as version 1, `draft`, whatever it was copied from, and is completely
 * independent thereafter — which is also why the source is recorded as provenance rather than as
 * a link that could break.
 */
export function useClonePointsSystem(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, version }: { id: string; name: string | null; version: number | null }) =>
      api.pointsSystems.clone(id, { roomId: roomId ?? '', name, version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

export function usePatchPointsSystem(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, description }: { id: string; name?: string; description?: string }) =>
      api.pointsSystems.patch(id, { name: name ?? null, description: description ?? null }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

/**
 * A 409 here is not a failure to report as one: it means some leaderboard has pinned a version,
 * and §2.4's answer is Archive. The server's message leads with exactly that, so the caller shows
 * it rather than paraphrasing.
 */
export function useDeletePointsSystem(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pointsSystems.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

export function useSetPointsSystemArchived(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? api.pointsSystems.archive(id) : api.pointsSystems.unarchive(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

export function useCreatePointsSystemVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, copyFromVersion }: { id: string; copyFromVersion: number | null }) =>
      // No previewScope: §2.1 says a test scope is not carried across versions, and the
      // designer sets one on the new draft's first save.
      api.pointsSystems.createVersion(id, {
        copyFromVersion,
        definition: null,
        previewScope: null,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

export function useSavePointsSystemVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      n,
      definition,
      previewScope,
    }: {
      id: string;
      n: number;
      definition: unknown;
      /** §2.1's test scope, written to its own column. Null clears it. */
      previewScope: unknown;
    }) => api.pointsSystems.putVersion(id, n, { definition, previewScope }),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['points-systems', roomId] });
      void qc.invalidateQueries({ queryKey: ['points-system-version', variables.id] });
    },
  });
}

/** The freeze. After this the version may never change, and a leaderboard may pin it. */
export function usePublishPointsSystemVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.pointsSystems.publishVersion(id, n),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['points-systems', roomId] });
      void qc.invalidateQueries({ queryKey: ['points-system-version', variables.id] });
    },
  });
}

export function useDeletePointsSystemVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.pointsSystems.removeVersion(id, n),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['points-systems', roomId] }),
  });
}

/**
 * The designer's live board: a definition scored over this room's real results, saving nothing.
 *
 * Keyed on the definition itself, so returning to a shape already scored costs no request at all
 * — which matters, because this is the most expensive thing the API does and it is rate-limited
 * per caller. `retry: false` for the same reason: the one failure worth expecting is that limit,
 * and retrying into it is how a caller stays refused.
 */
export function usePointsPreview({
  definition,
  scope,
  roomId,
  enabled,
}: {
  definition: unknown;
  /** §7.3's scope, sent beside the definition rather than inside it (§3.2). */
  scope: unknown;
  roomId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    // Both documents are in the key, because both change the answer — a board cached against one
    // scope must not be shown for another.
    queryKey: ['points-preview', roomId, JSON.stringify(definition), JSON.stringify(scope)],
    queryFn: () =>
      api.pointsSystems.preview({
        definition,
        roomId: roomId ?? '',
        scope,
        eventIds: null,
        limit: 100,
        // No drill-down with the board: the working for one player is its own request, made when
        // a row is opened. See `usePointsPreviewBreakdown`.
        breakdownPlayerId: null,
      }),
    enabled: enabled && !!roomId,
    staleTime: Infinity,
    retry: false,
    // Keeps the last board on screen while the next is worked out, rather than blanking a
    // 200-row table on every edit.
    placeholderData: sameRoom(roomId),
  });
}

/**
 * §7.4's working for one player of the previewed board, fetched when a standings row is opened.
 *
 * Keyed under the board's own key plus the player, so it is cached per player against the exact
 * definition and scope that produced the row — opening the same row twice costs no request, and an
 * edit to either document refetches the open panel rather than leaving working that explains a
 * total no longer on screen.
 *
 * `limit: 1` because the board is already rendered: what this call is for is the `breakdown` beside
 * it, and asking for a hundred standings rows a second time is the one part of the payload nobody
 * would read. It spends a permit from the preview endpoint's per-caller allowance, which is why it
 * fires on a click rather than travelling with every keystroke's board.
 */
export function usePointsPreviewBreakdown({
  definition,
  scope,
  roomId,
  playerId,
  enabled,
}: {
  definition: unknown;
  scope: unknown;
  roomId: string | null;
  /** Whose working to fetch, or null when no row is open. */
  playerId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    queryKey: [
      'points-preview',
      roomId,
      JSON.stringify(definition),
      JSON.stringify(scope),
      'breakdown',
      playerId,
    ],
    queryFn: () =>
      api.pointsSystems
        .preview({
          definition,
          roomId: roomId ?? '',
          scope,
          eventIds: null,
          limit: 1,
          breakdownPlayerId: playerId,
        })
        // Null is an answer — the player is not on this board — and it has to survive as one
        // rather than being turned into an empty object the panel would render as a scoreless
        // season.
        .then((result) => result.breakdown),
    enabled: enabled && !!roomId && !!playerId,
    staleTime: Infinity,
    retry: false,
  });
}

/*
 * NO `usePlatformSettings` HERE, and its absence is the point.
 *
 * There was one, unused since the initial commit, commented "drives the default rating board's
 * cadence" — a job the SERVER took over. Locked rule 4 resolves an inherited cadence on the read,
 * so `RatingBoardDto.effectiveRecalcCron` is what `cadenceLine` renders, and a second client-side
 * reader of the settings row would be a second answer to a question the DTO already answers.
 * Editing platform settings is admin-web's screen, where the hook lives and has a caller.
 *
 * Found by `queries.reachability.test.js` on its first run. Deleted rather than exempted: the
 * allow-list there is for work that is deferred, not for code nothing needs.
 */

// ---------- rating boards ----------

// ---- rating boards (docs/specs/ratings.md) -------------------------------------------------

/**
 * The room's rating boards, plus every platform template (§2.3).
 *
 * Keyed `['rating-boards', roomId]`. It was deliberately not `['rating-groups']` while the legacy
 * tag-filtered grouping still existed — one key for two shapes is how a cache hands a component
 * the wrong one — and that grouping retired at Phase 4b, so this is now the only ratings key.
 *
 * **Returns the room's boards AND every platform template** (§2.3, a null `roomId`). A caller
 * counting "this room's ratings" filters, which is what the top-navigation count does.
 */
export function useRatingBoards(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-boards', roomId],
    queryFn: () => api.ratingBoards.list(roomId ?? undefined),
    enabled: !!roomId,
  });
}

/**
 * §1.1's steps 8–12, computed.
 *
 * **A query although the endpoint is a POST**, because from the screen's point of view it is a
 * read: it persists nothing, the same board and anchor always answer the same thing, and the
 * caching a query gives is what stops a re-render re-running the most expensive operation this
 * feature has. The POST is the server's choice (see the client), not a mutation.
 *
 * **The anchor is in the key.** §2.2 makes it an input rather than a detail — the same board at two
 * anchors is two different boards — so a cache keyed without it would show yesterday's answer for
 * today's question and call it fresh.
 *
 * `retry: false` for the reason the points preview uses it: several of the refusals here are
 * permanent for this board (a template with no room, a visibility this release does not serve, no
 * points system selected), and retrying into one is how a caller stays refused while paying for it.
 */
export function useRatingStandings(
  boardId: string | null,
  asOf?: string,
  /**
   * <b>An opt-out, defaulting to on, and the default is right for the builder's preview</b> — which
   * runs this on a Draft deliberately, because previewing what a board *would* say is the whole
   * point of that pane.
   *
   * The standings screen passes false until it knows there is a published snapshot beside it: every
   * consumer of this answer there sits inside the "there is a snapshot" branch, so running the
   * engine over the room's whole history before then is the most expensive operation in the feature
   * performed to fill a variable nothing reads.
   */
  enabled = true,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-standings', boardId, asOf ?? 'today'],
    queryFn: () => api.ratingBoards.compute(boardId ?? '', asOf),
    enabled: !!boardId && enabled,
    retry: false,
  });
}

export function useCreateRatingBoard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRatingBoardRequest) => api.ratingBoards.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

/**
 * §9.2's save. The whole board, replaced — there is no PATCH, and the absence is the enforcement:
 * a board does not move between rooms and §2.4's transitions each have consequences a settable
 * column could reach past.
 *
 * Invalidates the standings and the snapshot as well as the list, because every one of the five
 * blocks changes what the board computes — §2.4 keeps them editable while Running, so a save on a
 * live board really does mean the live figures beside it are about a configuration that no longer
 * exists. The snapshot itself does not move (§8 only recalculation writes one), and that difference
 * is what `GET /drift` reports.
 */
export function useUpdateRatingBoard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PutRatingBoardRequest }) =>
      api.ratingBoards.update(id, body),
    onSuccess: (board) => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rating-standings', board.id] });
      void qc.invalidateQueries({ queryKey: ['rating-drift', board.id] });
    },
  });
}

/**
 * §2.4's Draft → Running, which is what pins the points system version.
 *
 * Invalidates the standings as well as the list, because starting changes which of §4.7's rules
 * decides who may read them: a Draft board is Private whatever it stores, so the answer to
 * "compute this" is genuinely different afterwards.
 */
/**
 * An export's download (docs/specs/export.md EX25): the file the server builds for a view, saved through an object URL.
 * A mutation rather than a query — it is a request the operator makes on purpose, and its answer is a file, not state
 * to cache. It invalidates nothing: an export changes no product data, and its audit record is read by no room-web
 * screen. No retry, the mutation default: a refusal is an answer, and a second export would be a second audit record.
 */
export function useExportDownload() {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ target, choice, fallbackName }: { target: ExportTarget; choice: ExportChoice; fallbackName: string }) => {
      const file = await api.exports.download(target, choice, fallbackName);
      saveFile(file);
      return file.fileName;
    },
  });
}

export function useStartRatingBoard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.ratingBoards.start(id),
    onSuccess: (board) => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rating-standings', board.id] });
      void qc.invalidateQueries({ queryKey: ['rating-snapshot', board.id] });
    },
  });
}

/**
 * §8's published standings: **the latest snapshot**, which is what a room reads.
 *
 * Deliberately a different key and a different call from `useRatingStandings`, which runs the
 * engine live. One answers what the rating *does* say and the other what it *would* say — and the
 * screen shows the first, because standings a room has published must not move under whoever is
 * reading them.
 *
 * <b>It always answers a record, and a null `sequenceNumber` is "nothing published".</b> That is an
 * ordinary state, not an error, and it reads differently on screen from "nobody scored". It used to
 * answer a nullable snapshot, which could not be said over HTTP — ASP.NET writes no body for a null
 * value, and the transport could only treat a bodiless 200 as a broken response.
 *
 * <b>Not asked for a board that cannot have one.</b> `publishesStandings` is the rule: a draft is
 * refused by both recalculate and close, so no snapshot can exist behind one and the request's
 * answer is known before it is sent.
 *
 * <b>The staff read, with each player's card</b> (gap 131): the anonymous `standings` carries none, and this table
 * shows the card and searches by it.
 */
export function useRatingSnapshot(boardId: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-snapshot', boardId],
    queryFn: () => api.ratingBoards.staffStandings(boardId ?? ''),
    enabled: !!boardId && enabled,
    retry: false,
  });
}

/**
 * §7. One player's rating taken apart — period by period, with every result that bucketed in and the
 * points engine's own working nested inside each one.
 *
 * <b>Keyed under the snapshot, which is what makes "fetched once" structural.</b> The sequence number
 * is part of the key, so a recalculation produces a different key rather than staling this one — and
 * a frozen snapshot's breakdown is frozen too, which is why `staleTime: Infinity` is correct here
 * rather than optimistic. Reopening a row therefore costs no request, at any remove: the global
 * 30-second `staleTime` the leaderboard breakdown leans on is a window, and this is a guarantee.
 *
 * <b>The board id comes before the discriminator</b>, so `invalidateQueries(['rating-snapshot', id])`
 * — which every recalculation already does — reaches every player's breakdown of that board. Keyed
 * the other way round it would silently never refetch while everything else on the screen updated.
 *
 * Fetched only when a row is opened. A board is hundreds of players and nobody reads more than one.
 *
 * <b>`gcTime` is left at its default five minutes and is deliberately not `Infinity`.</b> So a row
 * reopened straight away costs no request — which is the requirement — and one reopened after five
 * minutes with nothing else observing it does cost one, because by then the document is no longer in
 * memory and re-reading it is the only honest thing left to do. Holding every breakdown a board's
 * operator ever opened would be an unbounded amount of `nvarchar(max)` in a tab somebody leaves open
 * all day.
 */
export function useRatingBreakdown(
  boardId: string | null,
  sequenceNumber: number | null,
  playerId: string | null,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-snapshot', boardId, sequenceNumber, 'breakdown', playerId],
    queryFn: () => api.ratingBoards.breakdown(boardId ?? '', playerId ?? ''),
    enabled: !!boardId && !!playerId && sequenceNumber !== null,
    staleTime: Infinity,
  });
}

/**
 * §8.1's drift, **asked for only by an administrator** — the endpoint is admin-only and §8.1 asks
 * for it as an admin alert, so firing it as an operator would put a permanent 403 notice on the
 * screen, which reads as a broken app rather than as a report they do not have.
 *
 * Same arrangement as `useLeaderboardDrift`, and for the same reason it takes the flag rather than
 * catching the 403: a request you know will be refused is one not to send.
 */
export function useRatingDrift(boardId: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['rating-drift', boardId],
    queryFn: () => api.ratingBoards.drift(boardId ?? ''),
    enabled: !!boardId && enabled,
    retry: false,
  });
}

/**
 * §8's recalculate-now.
 *
 * Invalidates the snapshot **and** the board: a recalculation can be the one that closes a board
 * (§8.2's override is written final), and the card beside it reports the status.
 */
export function useRecalculateRatingBoard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string | null }) =>
      api.ratingBoards.recalculate(id, reason),
    onSuccess: (snapshot) => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rating-snapshot', snapshot.boardId] });
      void qc.invalidateQueries({ queryKey: ['rating-drift', snapshot.boardId] });
      void qc.invalidateQueries({ queryKey: ['room-activity', roomId] });
    },
  });
}

/** §2.5's Running → Closed, which writes the final snapshot. */
export function useCloseRatingBoard(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.ratingBoards.close(id),
    onSuccess: (board) => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rating-snapshot', board.id] });
      void qc.invalidateQueries({ queryKey: ['rating-drift', board.id] });
      void qc.invalidateQueries({ queryKey: ['room-activity', roomId] });
    },
  });
}

/**
 * §2.2's recalculation trigger.
 *
 * Invalidates the board list rather than only the board, because the cadence a screen shows is
 * partly the platform's (locked rule 4) and is resolved server-side on every read — so the answer
 * that comes back is the one to render, never a value the client assembled.
 */
export function useSetRatingRecalculation(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode, cron }: { id: string; mode: string; cron: string | null }) =>
      api.ratingBoards.setRecalculation(id, { recalcMode: mode, recalcCron: cron }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rating-boards', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['room-activity', roomId] });
    },
  });
}

// ---------- fantasy ----------

/** Teams entered in a contest. Upsert is keyed on (contestId, userId). */
export function useFantasyTeams(contestId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-teams', contestId],
    queryFn: () => api.fantasyTeams.list(contestId!),
    enabled: !!contestId,
  });
}

export function useCreateFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFantasyContestRequest) => api.fantasyContests.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

// Every field explicitly null: these PATCH contracts are non-optional-with-null, where null
// means "leave unchanged", so a member omitted here would be a missing_field rather than a
// no-op. Reshaped by the §5 redesign along with the DTO.
const EMPTY_CONTEST_PATCH: PatchFantasyContestRequest = {
  name: null,
  description: null,
  rosterFormat: null,
  rosterSize: null,
  budget: null,
  budgetUnitLabel: null,
  captainEnabled: null,
  captainMultiplier: null,
  entryFee: null,
  currency: null,
  maxEntriesPerPlayer: null,
  leaderboardId: null,
  salaryModelVersionId: null,
  // Not `null`: this one is a plain bool, so "say nothing" is false rather than absent — the same
  // shape `clearGuarantee` has on the tournament patch.
  clearSalaryModelVersion: false,
  status: null,
};

export function usePatchFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PatchFantasyContestRequest> }) =>
      api.fantasyContests.patch(id, { ...EMPTY_CONTEST_PATCH, ...body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

export function useDeleteFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fantasyContests.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

/**
 * §8.2's pool — the priced list a contest freezes when it publishes.
 *
 * Keyed under the contest rather than the room: it is one contest's document, and every write to
 * it invalidates exactly this.
 */
export function useFantasyPool(contestId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-pool', contestId],
    queryFn: () => api.fantasyContests.pool.get(contestId!),
    enabled: !!contestId,
  });
}

export function useSaveFantasyPool(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, players }: { id: string; players: PutFantasyPoolRequest['players'] }) =>
      api.fantasyContests.pool.put(id, { players }),
    onSuccess: (_pool, { id }) => {
      void qc.invalidateQueries({ queryKey: ['fantasy-pool', id] });
      void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

/**
 * §8.2's `POST /pool/derive`: run a salary model over the room's history and answer with a
 * proposal.
 *
 * <b>A mutation rather than a query, though it writes nothing.</b> It is a POST, it is expensive,
 * and — the part that matters — it is something the operator *asks for* rather than something the
 * screen does on their behalf. A query keyed on the model would re-run on every remount and
 * silently replace prices somebody was in the middle of adjusting; the merge that protects an
 * override (`mergeDerived`) is only safe because it happens on a click.
 *
 * It shares the points preview's per-caller rate-limit bucket, so `retry: false` for the reason
 * the salary preview has it: the one failure worth expecting is that limit, and retrying into it
 * is how a caller stays refused.
 */
export function useDeriveFantasyPool() {
  const api = useApi();
  return useMutation({
    mutationFn: ({
      id,
      salaryModelId,
      version,
      leaderboardIds,
    }: {
      id: string;
      salaryModelId: string;
      version: number | null;
      leaderboardIds: readonly string[] | null;
    }) =>
      api.fantasyContests.pool.derive(id, {
        salaryModelId,
        version,
        leaderboardIds: leaderboardIds && leaderboardIds.length > 0 ? [...leaderboardIds] : null,
      }),
    retry: false,
  });
}

/**
 * §6's three transitions, each its own hook because each is its own route with its own refusals.
 *
 * <b>They deliberately are not one `useTransition({ action })`.</b> Publish takes a body nothing
 * else does, lock and close take none, and every one of them has a different failure worth showing
 * — `event_already_started` on publish is rendered as a named list rather than as a toast. Folding
 * them into one mutation would make the error handling a switch over the action anyway, and would
 * put all three into one `isPending`, so locking would grey out close.
 *
 * All three invalidate the pool as well as the list: publishing is what makes it immutable, and a
 * screen still offering an edit control over frozen rows is the worst version of this bug.
 */
export function usePublishFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PublishFantasyContestRequest }) =>
      api.fantasyContests.publish(id, body),
    onSuccess: (_contest, { id }) => invalidateContest(qc, roomId, id),
  });
}

export function useLockFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fantasyContests.lock(id),
    onSuccess: (_contest, id) => invalidateContest(qc, roomId, id),
  });
}

export function useCloseFantasyContest(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.fantasyContests.close(id),
    onSuccess: (_contest, id) => invalidateContest(qc, roomId, id),
  });
}

/**
 * §6.2's drift report, **administrator-only**.
 *
 * `enabled` carries both halves of "is this worth asking": the caller is an administrator, and the
 * contest is Locked. An operator's screen firing this would meet a permanent 403, which reads as a
 * broken app rather than as a report they do not have — the same reasoning as the leaderboard's own
 * drift query. And a Draft, Open or Closed contest has no frozen scope to have drifted from, so
 * asking costs a scope resolution to learn nothing.
 *
 * No polling and no refetch on focus. Drift changes when the room changes the board, which is an
 * operator action inside this app — the invalidation on a recalculation is what refreshes it.
 */
export function useFantasyContestDrift(contestId: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery({
    // Keyed like its siblings (`fantasy-pool`, `fantasy-standings`) rather than under the contest
    // list's key, whose scoping id is the *room*. A discriminator appended after a contest id there
    // would never be reached by the list's invalidation, so it is invalidated explicitly instead.
    queryKey: ['fantasy-drift', contestId],
    queryFn: () => api.fantasyContests.drift(contestId!),
    enabled: enabled && contestId !== null,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

/**
 * §6.2's Adopt. Invalidates the whole contest, not only the drift: adopting rewrites the frozen
 * scope, writes a snapshot, moves the standings and sets `amendedAfterLock` — so every read on the
 * screen is stale at once, and refreshing them one at a time is how a screen ends up half updated.
 */
export function useAdoptFantasyScope(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      api.fantasyContests.adoptScope(vars.id, { reason: vars.reason }),
    onSuccess: (_snapshot, vars) => invalidateContest(qc, roomId, vars.id),
  });
}

/**
 * §8.3's standings — the latest snapshot, served rather than computed.
 *
 * Only worth asking for once a contest is locked: a draft or an open contest has no snapshot and
 * the answer is an empty list with a null sequence number, which is a request spent to learn
 * nothing. The caller passes `enabled` rather than this deciding, because "which states have
 * standings" is §6's business and lives in `fantasy/contest.ts`.
 */
export function useFantasyStandings(contestId: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['fantasy-standings', contestId],
    queryFn: () => api.fantasyContests.standings(contestId!),
    enabled: enabled && !!contestId,
  });
}

/**
 * A transition changes the contest, its pool's editability and its standings at once, so all
 * three go. Extracted because three hooks doing it identically is three places for one of them to
 * be forgotten — which for the pool would leave an edit control over frozen rows.
 */
function invalidateContest(
  qc: ReturnType<typeof useQueryClient>,
  roomId: string | null,
  id: string,
) {
  void qc.invalidateQueries({ queryKey: ['fantasy-contests', roomId] });
  invalidateRoomSummary(qc, roomId);
  void qc.invalidateQueries({ queryKey: ['fantasy-pool', id] });
  void qc.invalidateQueries({ queryKey: ['fantasy-standings', id] });
  void qc.invalidateQueries({ queryKey: ['fantasy-teams', id] });

  // §6.2. Adopting rewrites the frozen scope, and recalculating or closing moves the standings the
  // drift report compares against — so every one of these leaves the report stale.
  void qc.invalidateQueries({ queryKey: ['fantasy-drift', id] });
}

// ---------- players & room settings ----------

/**
 * The player import's two routes bound to the room (ADR 0019), as `createImportRun` calls them, and what a saved
 * part changes on screen (§9): every player read of the room, the room summary, the room's tournaments (a
 * tournament's read names its finishers) and the room list's player count. A run calls `afterSave` once per part
 * that landed — never for one refused or with nothing to write.
 */
export function usePlayerImport(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMemo(() => ({
    api: {
      preview: (body: PreviewPlayerImportRequest) => api.players.importPreview(roomId ?? '', body),
      commit: (body: CommitPlayerImportRequest) => api.players.importCommit(roomId ?? '', body),
    },
    afterSave: () => {
      void qc.invalidateQueries({ queryKey: ['players', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  }), [api, qc, roomId]);
}

export function useCreatePlayer(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlayerRequest) => api.players.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['players', roomId] });
      invalidateRoomSummary(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/**
 * Every field explicitly null — "leave it alone" — so a form only has to name what it changed.
 * Same shape as EMPTY_ROOM_PATCH below, and the same reason: a patch built from a form that never
 * showed a field would otherwise send whatever the form's own default happened to be.
 *
 * The two clear flags are absent rather than false, because false is what the server assumes and
 * spelling it out here invites somebody to set one from a blank input — which is precisely what
 * the flags exist to stop.
 */
const EMPTY_PLAYER_PATCH: PatchPlayerRequest = {
  firstName: null,
  lastName: null,
  city: null,
  state: null,
  avatar: null,
  email: null,
  phone: null,
};

/**
 * Edits the room's record of one of its own players.
 *
 * Invalidates on the `['players', roomId]` prefix, which reaches the plain list, the contact
 * records and the coverage count at once — a name change moves what every results table shows,
 * and a contact change moves the coverage number this screen prints. And the room's tournaments,
 * because a tournament's read names its finishers (performance rank 2): an open one must show the
 * new name, not the old.
 */
export function usePatchPlayer(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PatchPlayerRequest> }) =>
      api.players.patch(roomId ?? '', id, { ...EMPTY_PLAYER_PATCH, ...body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['players', roomId] });
      void qc.invalidateQueries({ queryKey: ['tournaments', roomId] });
      invalidateRoomSummary(qc, roomId);
    },
  });
}

const EMPTY_ROOM_PATCH: PatchRoomRequest = {
  name: null,
  location: null,
  status: null,
  timeZone: null,
  primaryColor: null,
  accentColor: null,
  logo: null,
  canLeaderboard: null,
  canFantasy: null,
  canRatings: null,
  defaultTags: null,
};

/** Rooms may only change their own default tags here; feature flags are admin-owned. */
export function usePatchRoom() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PatchRoomRequest> }) =>
      api.rooms.patch(id, { ...EMPTY_ROOM_PATCH, ...body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

// ---------- salary models (this room's own, plus the platform's) ----------

/**
 * What this room may price a contest with: its own models and every platform one, in a single
 * ordered list. The server merges them, exactly as it does for points systems — one query, not
 * two lists the client has to reconcile and sort for itself.
 */
export function useSalaryModels(roomId: string | null, includeArchived: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['salary-models', roomId, { includeArchived }],
    queryFn: () => api.salaryModels.listForRoom(roomId ?? '', includeArchived),
    enabled: !!roomId,
  });
}

/**
 * One version's definition. The list carries only summaries — number, status, dates — since a
 * list of models has no business shipping every stored definition with it.
 */
export function useSalaryModelVersion(modelId: string | null, versionNumber: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['salary-model-version', modelId, versionNumber],
    queryFn: () => api.salaryModels.versions.get(modelId ?? '', versionNumber ?? 0),
    enabled: !!modelId && versionNumber !== null,
    // A published version is frozen and a draft only changes from the builder, which invalidates
    // on save. Nothing else moves it underneath us.
    staleTime: 5 * 60_000,
  });
}

export function useCreateSalaryModel(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSalaryModelRequest) =>
      api.salaryModels.createForRoom(roomId ?? '', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

/**
 * §4.1's answer to a platform model an operator wants to change: take a copy this room owns.
 *
 * The copy lands as version 1, `draft`, whatever it was copied from, and is independent
 * thereafter — which is why the source is recorded as provenance rather than as a link that could
 * break. The same route, and the same semantics, as cloning a points system.
 */
export function useCloneSalaryModel(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      name,
      version,
    }: {
      id: string;
      name: string | null;
      version: number | null;
    }) => api.salaryModels.clone(id, { roomId: roomId ?? '', name, version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

export function usePatchSalaryModel(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, description }: { id: string; name?: string; description?: string }) =>
      api.salaryModels.patch(id, { name: name ?? null, description: description ?? null }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

/**
 * A 409 here is not a failure to report as one: it means a contest has pinned a version, and the
 * answer is Archive. The server's message leads with exactly that, so the caller shows it rather
 * than paraphrasing.
 */
export function useDeleteSalaryModel(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.salaryModels.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

export function useSetSalaryModelArchived(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? api.salaryModels.archive(id) : api.salaryModels.unarchive(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

export function useCreateSalaryModelVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, copyFromVersion }: { id: string; copyFromVersion: number | null }) =>
      // No previewScope: a test window is not carried across versions (§4.1), and the designer
      // sets one on the new draft's first save.
      api.salaryModels.versions.create(id, {
        copyFromVersion,
        definition: null,
        previewScope: null,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

export function useSaveSalaryModelVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      n,
      definition,
      previewScope,
    }: {
      id: string;
      n: number;
      definition: unknown;
      /** The designer's own state, written to its own column. Null clears it. */
      previewScope: unknown;
    }) => api.salaryModels.versions.put(id, n, { definition, previewScope }),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['salary-models', roomId] });
      void qc.invalidateQueries({ queryKey: ['salary-model-version', variables.id] });
    },
  });
}

/** The freeze. After this the version may never change, and a contest may pin it. */
export function usePublishSalaryModelVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.salaryModels.versions.publish(id, n),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ['salary-models', roomId] });
      void qc.invalidateQueries({ queryKey: ['salary-model-version', variables.id] });
    },
  });
}

export function useDeleteSalaryModelVersion(roomId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.salaryModels.versions.remove(id, n),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary-models', roomId] }),
  });
}

/**
 * §8.1's live pool: a definition priced over this room's real history, saving nothing.
 *
 * Keyed on the definition and the boards it was told to price from, so returning to a shape
 * already derived costs no request — which matters, because this **shares the points preview's
 * per-caller rate-limit bucket** by design: the two are the same kind of read over the same room's
 * history, driven by the same debounced editor. `retry: false` for the same reason: the one
 * failure worth expecting is that limit, and retrying into it is how a caller stays refused.
 */
export function useSalaryPreview({
  definition,
  leaderboardIds,
  roomId,
  enabled,
}: {
  definition: unknown;
  /** The designer's override of the document's own lookback. Empty defers to the document. */
  leaderboardIds: readonly string[];
  roomId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    queryKey: ['salary-preview', roomId, JSON.stringify(definition), [...leaderboardIds].sort()],
    queryFn: () =>
      api.salaryModels.preview({
        roomId: roomId ?? '',
        definition,
        leaderboardIds: leaderboardIds.length > 0 ? [...leaderboardIds] : null,
        // The whole pool is always derived and counted server-side; this truncates only the rows
        // that come back. 250 is comfortably past §4.5's 100–200 target, so the warning about pool
        // size is never computed from a list this cut short.
        limit: 250,
      }),
    enabled: enabled && !!roomId,
    staleTime: Infinity,
    retry: false,
    // Keeps the last pool on screen while the next is worked out, rather than blanking a
    // 150-row table on every edit.
    placeholderData: sameRoom(roomId),
  });
}
